/**
 * TRANSCOST-1 · 接机 / 送机成本（每趟全包·含油 + 司机·一车一趟一个价，不分人头）的共用小件。
 * 线路默认（路线配置）、新建产品第 1 步、产品详情「接送机」卡三处用同一个输入，填法和车辆「车队日成本」一样（$ 金额 + 「/ 趟」后缀）。
 * 取值（产品填的 / 线路默认 / 未填）由后端 transfer_modes.transfer_cost 给，前端不自己推。
 */
import { useAuthStore } from "@/store/authStore";
import type { TransferCostSource } from "@/services/types/products";

import { DEFAULT_TRANSFER_TRIP_COST_CENTS } from "./routeBinding";
export { DEFAULT_TRANSFER_TRIP_COST_CENTS };
/** 产品成本表按「每趟 ÷ 2 人估」（后端 TRANSFER_COST_PAX_ESTIMATE）；订单整趟进单、不分人头。 */
export const TRANSFER_COST_PAX_ESTIMATE = 2;

export const COST_SOURCE_LABEL: Record<TransferCostSource, string> = {
  none: "不含", product: "本产品填的", line: "来自线路默认", unset: "未填",
};

const COST_VISIBLE_ROLES = new Set(["owner", "finance", "tech_admin"]);

/** 成本字段只给成本可见角色（口径⑦·同后端 pricing._can_see_cost）；其它角色整块不显示成本。 */
export function useCanSeeCost(): boolean {
  const me = useAuthStore((s) => s.user);
  return !!me && ((me.roleCodes ?? []).some((r) => COST_VISIBLE_ROLES.has(r)) || COST_VISIBLE_ROLES.has(me.role));
}

/** COSTADJ-1 · 午餐餐标 / 车队日成本单独调整：输入框文字 → 美分。空 = null（没填 / 跟随）；0 是合法值（午餐免费 ≠ 没填）；负数 / 非数 = undefined（不合法）。 */
export function usdInputToCentsAllowZero(text: string): number | null | undefined {
  const t = text.trim();
  if (!t) return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return Math.round(n * 100);
}

export const centsToUsdInput = (c: number | null | undefined): string => (c == null ? "" : String(Math.round(c) / 100));
/** 输入框文字 → 美分；空 / 非法 / ≤ 0 → null（不拿 0 冒充已填，同后端 gt=0）。 */
export function usdInputToCents(v: string): number | null {
  if (v.trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : null;
}
export const fmtUsd = (c: number | null | undefined): string => (c == null ? "—" : `$${(c / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`);

/**
 * 一个「$ 金额 / 趟（含油 + 司机）」输入。
 *  · ``inheritedCents`` = 线路默认：产品没自己填时显示「来自线路 $X」，点「改」才展开输入；
 *  · 没有线路默认 → 直接是输入框，预填 $60（由调用方在选接机 / 送机时把 state 置为 DEFAULT，这里只管显示）。
 */
export function TransferCostInput({ label, testId, cents, onCents, inheritedCents, required = true, disabled = false, unfilledNote }: {
  label: string; testId: string; cents: number | null; onCents: (c: number | null) => void;
  inheritedCents?: number | null; required?: boolean; disabled?: boolean; unfilledNote?: string;
}) {
  const inherits = cents == null && inheritedCents != null;
  return (
    <div className="flex flex-col gap-1" data-testid={`${testId}-field`}>
      <span className="text-xs font-medium text-slate-600">{label}{required && <span className="text-rose-600"> *</span>}</span>
      {inherits ? (
        <div className="flex items-center gap-2 text-[13px]">
          <b className="text-slate-800" data-testid={`${testId}-inherited`}>{fmtUsd(inheritedCents)} / 趟</b>
          <span className="rounded bg-slate-100 px-1.5 py-px text-[11px] text-slate-400">{COST_SOURCE_LABEL.line}</span>
          {!disabled && (
            <button type="button" onClick={() => onCents(inheritedCents ?? DEFAULT_TRANSFER_TRIP_COST_CENTS)}
              className="rounded-md border border-slate-300 bg-white px-2 py-0.5 text-[12px] font-medium text-slate-600 hover:bg-slate-50">改</button>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-md border border-slate-300 bg-white focus-within:border-blue-500">
            <span className="pl-2.5 text-[13px] text-slate-400">$</span>
            <input data-testid={testId} type="number" min={0} step={1} disabled={disabled} aria-label={label}
              value={centsToUsdInput(cents)} onChange={(e) => onCents(usdInputToCents(e.target.value))}
              placeholder="如 60" className="w-24 bg-transparent px-1.5 py-1.5 text-[13px] outline-none" />
          </div>
          <span className="text-[12.5px] text-slate-500">/ 趟（含油 + 司机）</span>
          {cents == null && required && <span className="text-[12px] font-medium text-rose-600" data-testid={`${testId}-missing`}>{unfilledNote ?? "必填"}</span>}
        </div>
      )}
    </div>
  );
}
